/**
 * #933 — Settings → Operations card.
 *
 * The four numbers the booking engine and the customer app run on, set by
 * the owner at the desk instead of by a script a Claude session runs:
 * total dogs on site (Ted A6, a hard stop), the vaccination booking gate
 * (#773), meal-tracking visibility to pet parents (#721 / A4), and the
 * staffing ratio (A5, recorded now, consumed by the daily rollup #740).
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: every value shown is the value the server
 *    resolves for the booking path (the GET returns the resolver's output,
 *    not the raw blob), with a "Platform default" mark where the owner has
 *    never set it.
 *  - #2 The next action is obvious: one primary Save, enabled only when
 *    something changed; a non-owner sees the numbers and a plain reason,
 *    never a dead control (#684 class).
 *  - #5 Never lie about data: loading is a skeleton, a failed load is an
 *    honest error with Retry, defaults are labelled as defaults, and the
 *    last change names who/when/what from the audit row — not a toast.
 *  - Five states: loading · error(+retry) · empty(all defaults, teaching
 *    copy) · ready · saving. Stale does not apply: the card reads fresh on
 *    every open and shows nothing it did not just fetch.
 *
 * Counter Test: Ted, at the desk, changes "Total dogs on site" from 40 to
 * 60, taps Save, and the next boarding create honors it. He never opens a
 * terminal or texts Johnny.
 *
 * #1104 adds "Vaccine durations": per vaccine, how the vaccination inbox
 * works out an expiry when the vet printed only the date given. Ted,
 * 2026-09-25: "For our records, Bordetella is one year, while DAPP and
 * rabies may be one or three years." Hidden on a server that does not send
 * `vaccineTerms` (frontends deploy first), never shown as a guess.
 *
 * #933 D2 — a manager saves this card too. The charges (meal tracking, late
 * pickup, multi-dog discount) stay the owner's: for a manager they read as
 * statements in place, never as a control the server would refuse. The two
 * grooming-day prices are the manager's to set.
 *
 * Server contract: GET/PUT /api/v2/admin/tenant/operations. Read is
 * business-config:read:all; write business-config:write:all (owner, manager),
 * the charging keys owner-only. The PUT is a PATCH — this card sends only the
 * keys that changed.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, Input, Select, Skeleton } from '@ferro/ui';
import {
  adminTenantServicesApi,
  adminTenantSettingsApi,
  api,
  type TenantMultiDogAppliesTo,
  type TenantOperationsKey,
  type TenantOperationsPatch,
  type TenantOperationsSettings,
  type TenantVaccinationGate,
  type TenantVaccineTermKey,
  type TenantVaccineTermPolicy,
  type TenantVaccineTerms,
} from '../../lib/api.js';
import { isLegOnlyServiceName } from '@ferro/core/modules/booking/transport-services.js';
import { OnlinePaymentsModeNote } from './OnlinePaymentsModeNote.js';
import { whenLabel } from './last-change-label.js';
// SPEC 2026-10-26 Story 1 — the required-vaccine list sits beside the gate.
import { VaccinationRequirementsSection } from './VaccinationRequirementsSection.js';

interface OperationsSettingsCardProps {
  apiUrl: string;
  /** Holds business-config:write:all (owner, manager). Everyone else sees read-only. */
  canEdit: boolean;
  /**
   * #933 D2 — may change what customers are charged (owner,
   * business-config.pricing:write:all). Defaults to `canEdit`.
   */
  canEditCharges?: boolean;
  /**
   * SPEC 2026-10-26 D1 — may add, edit and remove required vaccinations:
   * the owner, or a manager holding business-config.vaccinations:write:all.
   * Defaults to `canEdit`.
   */
  canEditVaccinations?: boolean;
}

type Status = 'loading' | 'error' | 'ready';

interface FormState {
  capacity: string;
  gate: TenantVaccinationGate;
  careLogParentVisible: boolean;
  ratio: string;
  /** #926 — Ted A4: the meal-tracking charge on boarding stays, per day (#1123). */
  mealCharge: boolean;
  /** Dollars, as typed ("5.00"). */
  mealRate: string;
  /** #914 — Ted #11: the boarding checkout cutoff and its grace, `HH:MM`. */
  lateCharge: boolean;
  boardingCheckout: string;
  boardingGrace: string;
  /** #1104 — null when the server does not carry the setting yet. */
  vaccineTerms: Record<TenantVaccineTermKey, VaccineTermChoice> | null;
  /** LG (#1113) — ticked service names; null when the server does not carry the setting yet. */
  commingling: string[] | null;
  /** LG (#1111) — the heat question switch; null when the server does not carry it yet. */
  heatRequired: boolean | null;
  /**
   * Ted #34 E6.1 (#942) — the groom-day half-day rate in dollars as typed
   * ("25.00"; blank = not offered). null when the server does not carry it.
   */
  groomDayRate: string | null;
  /** Ted #34 E6.2 — the first-groom discount in whole percent as typed (blank = off); null when absent. */
  firstGroomPercent: string | null;
  /**
   * #948 — the multi-dog discount: percent as typed (blank = off), the ticked
   * service names, and who gets it. Absent / null when the server does not
   * carry the keys yet (the control is hidden). Optional so older form
   * literals stay valid.
   */
  multiDog?: MultiDogForm | null;
  /**
   * Sawyer 10-30 — the services whose check-in does NOT ask to print a run
   * card. Null / absent when the server does not carry the key (hidden).
   */
  runCardOff?: string[] | null;
  /**
   * SPEC 2026-10-26 D4 — "Only records your staff have checked count for
   * online booking". null / absent when the server does not carry the key
   * (the control is hidden, exactly like `heatRequired`).
   */
  verifiedOnly?: boolean | null;
  /** SPEC 2026-10-26 Story 5 — vet name and phone before a first booking; null / absent = hidden. */
  vetRequired?: boolean | null;
}

export interface MultiDogForm {
  percent: string;
  services: string[];
  appliesTo: TenantMultiDogAppliesTo;
}

/**
 * #948 — the services offered in the multi-dog checklist when the tenant's
 * own catalogue cannot be read: Ted's three, the platform default.
 */
export const DEFAULT_MULTI_DOG_SERVICE_OPTIONS: ReadonlyArray<{ name: string; label: string }> = [
  { name: 'daycare', label: 'Daycare' },
  { name: 'daycare-half', label: 'Half-day daycare' },
  { name: 'boarding', label: 'Boarding' },
];

export const MULTI_DOG_APPLIES_TO_OPTIONS: ReadonlyArray<{
  value: TenantMultiDogAppliesTo;
  label: string;
  description: string;
}> = [
  {
    value: 'every_dog',
    label: 'Every dog',
    description: 'Two dogs from one family here together: both visits get the discount.',
  },
  {
    value: 'additional_dogs',
    label: 'Every dog after the first',
    description: 'The highest-priced dog pays full price; each other dog gets the discount.',
  },
];

/**
 * LG (#1113) — the on-floor services an owner can mark as commingling. The
 * same list the facility headcount counts (daycare, half-day, boarding,
 * assessment): grooming and walks are never in a play yard.
 */
export const COMMINGLING_SERVICE_OPTIONS: ReadonlyArray<{ name: string; label: string }> = [
  { name: 'daycare', label: 'Daycare' },
  { name: 'daycare-half', label: 'Half-day daycare' },
  { name: 'boarding', label: 'Boarding' },
  { name: 'assessment', label: 'Assessment (meet & greet)' },
];
export const DEFAULT_COMMINGLING_SERVICE_NAMES: readonly string[] = ['daycare', 'daycare-half'];

function sameNames(a: readonly string[], b: readonly string[]): boolean {
  const x = [...new Set(a.map((n) => n.trim().toLowerCase()))].sort();
  const y = [...new Set(b.map((n) => n.trim().toLowerCase()))].sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
}

/**
 * #1104 — the four things an owner can mean, as one select value each.
 * Stored as the allowed term lengths in years.
 */
export type VaccineTermChoice = 'none' | '1' | '3' | '1,3';

export const VACCINE_TERM_KEYS: ReadonlyArray<TenantVaccineTermKey> = ['rabies', 'dhpp', 'bordetella'];

export const VACCINE_TERM_VACCINE_LABELS: Record<TenantVaccineTermKey, string> = {
  rabies: 'Rabies',
  dhpp: 'DAPP / DHPP (distemper)',
  bordetella: 'Bordetella',
};

export const VACCINE_TERM_OPTIONS: ReadonlyArray<{ value: VaccineTermChoice; label: string }> = [
  { value: 'none', label: "Don't calculate — staff enter the expiry" },
  { value: '1', label: 'Always 1 year' },
  { value: '3', label: 'Always 3 years' },
  { value: '1,3', label: '1 or 3 years — ask' },
];

/** What each choice does at the desk — the copy the owner decides by. */
export const VACCINE_TERM_DESCRIPTIONS: Record<VaccineTermChoice, string> = {
  none: 'When the record has no expiry printed, staff type it in.',
  '1': 'When the record has no expiry printed, it is set to one year after the date given.',
  '3': 'When the record has no expiry printed, it is set to three years after the date given.',
  '1,3':
    'Staff must pick 1-year or 3-year for each record before approving it, unless the record says. It is never guessed.',
};

/** Stored policy (years) → the select value. The server only ever sends one of the four canonical shapes. */
export function choiceFromPolicy(policy: TenantVaccineTermPolicy): VaccineTermChoice {
  const sorted = [...policy].sort((a, b) => a - b).join(',');
  if (sorted === '1') return '1';
  if (sorted === '3') return '3';
  if (sorted === '1,3') return '1,3';
  return 'none';
}

export function policyFromChoice(choice: VaccineTermChoice): TenantVaccineTermPolicy {
  switch (choice) {
    case '1':
      return [1];
    case '3':
      return [3];
    case '1,3':
      return [1, 3];
    case 'none':
      return [];
  }
}

function termChoicesFrom(
  terms: TenantVaccineTerms | undefined,
): Record<TenantVaccineTermKey, VaccineTermChoice> | null {
  if (!terms) return null;
  return {
    rabies: choiceFromPolicy(terms.rabies ?? []),
    dhpp: choiceFromPolicy(terms.dhpp ?? []),
    bordetella: choiceFromPolicy(terms.bordetella ?? []),
  };
}

/** Owner-facing names for the audit's changed keys (one phrasing, reused everywhere). */
export const OPERATIONS_KEY_LABELS: Record<TenantOperationsKey, string> = {
  defaultFacilityCapacity: 'total dogs on site',
  vaccinationBookingGate: 'vaccination gate',
  careLogParentVisible: 'meal tracking visibility',
  staffingDogsPerHandler: 'dogs per handler',
  mealTrackingChargeEnabled: 'meal tracking charge',
  mealTrackingRateCents: 'meal tracking rate',
  /** #939 — set on the Lodging tab, named here so the last-change line can say so. */
  lodgingCapacityThreshold: 'lodging capacity threshold',
  boardingLatePickupChargeEnabled: 'late pickup charge',
  boardingCheckoutLocalTime: 'boarding checkout time',
  boardingLateGraceLocalTime: 'late pickup time',
  vaccineTerms: 'vaccine durations',
  petInactivityMonths: 'pet inactivity rule', // Sawyer 09 pet-inactivity (D1, R4)
  comminglingServiceNames: 'services where dogs commingle',
  heatDeclarationRequired: 'heat question at reservation',
  groomDayHalfDaycareRateCents: 'half-day daycare with a groom',
  firstGroomDiscountPercent: 'first-groom discount',
  multiDogDiscountPercent: 'multi-dog discount',
  multiDogServices: 'multi-dog discount services',
  multiDogAppliesTo: 'who gets the multi-dog discount',
  runCardPromptOffServices: 'run card question at check-in',
  // Sawyer 06 beds-lockers-runcard (B2, B4) — set on the Lodging tab, named here for the last-change line.
  bedSizeClasses: 'bed sizes',
  lockerFollowsBed: 'locker follows bed',
  // SPEC 2026-10-26 (D4, Story 5)
  vaccinationVerifiedRecordsOnly: 'staff-checked records only',
  vetInfoRequiredForBooking: 'vet before a first booking',
};

/** #926 — what the card shows for a server that does not carry the setting yet. */
export const DEFAULT_MEAL_TRACKING_RATE_CENTS = 500;

/** #914 — Ted's numbers, shown when an older server omits the keys. */
export const DEFAULT_BOARDING_CHECKOUT_LOCAL_TIME = '10:30';
export const DEFAULT_BOARDING_LATE_GRACE_LOCAL_TIME = '11:00';
const LOCAL_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** `HH:MM` → minutes since midnight, or NaN when it is not a clock time. */
export function localTimeMinutes(value: string): number {
  const m = LOCAL_TIME_RE.exec(value.trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : Number.NaN;
}

function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}
/** "5" / "5.00" / " 5.5 " → 500 / 500 / 550; NaN when not a money amount. */
export function dollarsToCents(input: string): number {
  const trimmed = input.trim();
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return Number.NaN;
  return Math.round(Number(trimmed) * 100);
}

/**
 * What each gate mode means to a customer, in the customer's terms — the copy
 * the owner decides by (SPEC 2026-10-26 AC 1.6). "Required" means the list
 * under the gate; the desk is never stopped by any of them.
 */
export const GATE_DESCRIPTIONS: Record<TenantVaccinationGate, string> = {
  off: "Customers aren't told anything about vaccines when they book online.",
  warn: "Customers are told which required records are missing or out of date, and can still book.",
  block:
    "Customers can't book a dog online until its required records are current. Your staff can still book it at the desk.",
};

/** AC 1.6 — Block with an empty list stops nobody; say so where the owner is looking. */
export const GATE_BLOCK_EMPTY_NOTE = 'Block has no effect until you add a required vaccine.';

/** AC 1.8 — a manager sees the gate and the toggles, and why they cannot change them. */
export const OWNER_ONLY_NOTE = 'Only the owner can change this.';

export const VERIFIED_ONLY_LABEL = 'Only records your staff have checked count for online booking';
export const VERIFIED_ONLY_DESCRIPTION =
  'When on, a record a customer types in or sends waits until your staff check it. Records that came from Gingr or that your staff entered still count.';
export const VET_REQUIRED_LABEL = "Require vet name and phone before a dog's first booking";
export const VET_REQUIRED_DESCRIPTION =
  'Never asked at sign-up. Customers add it when they book online, and your staff are never stopped at the desk.';

const GATE_OPTIONS: ReadonlyArray<{ value: TenantVaccinationGate; label: string }> = [
  { value: 'off', label: 'Off — no vaccination check online' },
  { value: 'warn', label: 'Warn — show a warning, allow the booking' },
  { value: 'block', label: 'Block — refuse online bookings until shots are current' },
];

export function formFromSettings(s: TenantOperationsSettings): FormState {
  return {
    capacity: String(s.operations.defaultFacilityCapacity),
    gate: s.operations.vaccinationBookingGate,
    careLogParentVisible: s.operations.careLogParentVisible,
    ratio: s.operations.staffingDogsPerHandler === null ? '' : String(s.operations.staffingDogsPerHandler),
    mealCharge: s.operations.mealTrackingChargeEnabled === true,
    mealRate: centsToDollars(s.operations.mealTrackingRateCents ?? DEFAULT_MEAL_TRACKING_RATE_CENTS),
    lateCharge: s.operations.boardingLatePickupChargeEnabled === true,
    boardingCheckout:
      s.operations.boardingCheckoutLocalTime ?? DEFAULT_BOARDING_CHECKOUT_LOCAL_TIME,
    boardingGrace:
      s.operations.boardingLateGraceLocalTime ?? DEFAULT_BOARDING_LATE_GRACE_LOCAL_TIME,
    vaccineTerms: termChoicesFrom(s.operations.vaccineTerms),
    commingling: Array.isArray(s.operations.comminglingServiceNames) ? [...s.operations.comminglingServiceNames] : null,
    heatRequired: typeof s.operations.heatDeclarationRequired === 'boolean' ? s.operations.heatDeclarationRequired : null,
    // Ted #34 — `undefined` = an older server (control hidden); null = not set (blank).
    groomDayRate:
      s.operations.groomDayHalfDaycareRateCents === undefined
        ? null
        : s.operations.groomDayHalfDaycareRateCents === null
          ? ''
          : centsToDollars(s.operations.groomDayHalfDaycareRateCents),
    firstGroomPercent:
      s.operations.firstGroomDiscountPercent === undefined
        ? null
        : s.operations.firstGroomDiscountPercent === null
          ? ''
          : String(s.operations.firstGroomDiscountPercent),
    // #948 — `undefined` = an older server (control hidden); null percent = off (blank).
    multiDog:
      s.operations.multiDogDiscountPercent === undefined
        ? null
        : {
            percent:
              s.operations.multiDogDiscountPercent === null ? '' : String(s.operations.multiDogDiscountPercent),
            services: [...(s.operations.multiDogServices ?? DEFAULT_MULTI_DOG_SERVICE_OPTIONS.map((o) => o.name))],
            appliesTo: s.operations.multiDogAppliesTo ?? 'every_dog',
          },
    // Sawyer 10-30 — `undefined` = an older server (control hidden).
    runCardOff: Array.isArray(s.operations.runCardPromptOffServices)
      ? [...s.operations.runCardPromptOffServices]
      : null,
    // SPEC 2026-10-26 — `undefined` = an older server (control hidden).
    verifiedOnly:
      typeof s.operations.vaccinationVerifiedRecordsOnly === 'boolean'
        ? s.operations.vaccinationVerifiedRecordsOnly
        : null,
    vetRequired:
      typeof s.operations.vetInfoRequiredForBooking === 'boolean' ? s.operations.vetInfoRequiredForBooking : null,
  };
}

/** Only the keys whose value differs from what the server holds. */
export function patchFromForm(form: FormState, current: TenantOperationsSettings): TenantOperationsPatch {
  const patch: TenantOperationsPatch = {};
  const cap = Number(form.capacity);
  if (cap !== current.operations.defaultFacilityCapacity) patch.defaultFacilityCapacity = cap;
  if (form.gate !== current.operations.vaccinationBookingGate) patch.vaccinationBookingGate = form.gate;
  if (form.careLogParentVisible !== current.operations.careLogParentVisible) {
    patch.careLogParentVisible = form.careLogParentVisible;
  }
  const ratio = form.ratio.trim() === '' ? null : Number(form.ratio);
  if (ratio !== current.operations.staffingDogsPerHandler) patch.staffingDogsPerHandler = ratio;
  // #926 — an older server omits both keys; compare against what the card showed.
  const currentMealCharge = current.operations.mealTrackingChargeEnabled === true;
  if (form.mealCharge !== currentMealCharge) patch.mealTrackingChargeEnabled = form.mealCharge;
  const currentRate = current.operations.mealTrackingRateCents ?? DEFAULT_MEAL_TRACKING_RATE_CENTS;
  const rate = dollarsToCents(form.mealRate);
  if (Number.isInteger(rate) && rate !== currentRate) patch.mealTrackingRateCents = rate;
  // #914 — same "older server omits the key" comparison as #926 above.
  const currentLateCharge = current.operations.boardingLatePickupChargeEnabled === true;
  if (form.lateCharge !== currentLateCharge) patch.boardingLatePickupChargeEnabled = form.lateCharge;
  const currentCheckout =
    current.operations.boardingCheckoutLocalTime ?? DEFAULT_BOARDING_CHECKOUT_LOCAL_TIME;
  if (form.boardingCheckout.trim() !== currentCheckout) {
    patch.boardingCheckoutLocalTime = form.boardingCheckout.trim();
  }
  const currentGrace =
    current.operations.boardingLateGraceLocalTime ?? DEFAULT_BOARDING_LATE_GRACE_LOCAL_TIME;
  if (form.boardingGrace.trim() !== currentGrace) {
    patch.boardingLateGraceLocalTime = form.boardingGrace.trim();
  }
  // #1104 — only the vaccines whose choice moved; the server merges per
  // vaccine, so an untouched one keeps whatever it holds.
  const currentTerms = termChoicesFrom(current.operations.vaccineTerms);
  if (form.vaccineTerms && currentTerms) {
    const changed: Partial<TenantVaccineTerms> = {};
    for (const key of VACCINE_TERM_KEYS) {
      if (form.vaccineTerms[key] !== currentTerms[key]) {
        changed[key] = policyFromChoice(form.vaccineTerms[key]);
      }
    }
    if (Object.keys(changed).length > 0) patch.vaccineTerms = changed;
  }
  // LG — only when the server carries the keys (an older server never sees them).
  if (form.commingling && Array.isArray(current.operations.comminglingServiceNames)) {
    if (!sameNames(form.commingling, current.operations.comminglingServiceNames)) {
      patch.comminglingServiceNames = [...form.commingling];
    }
  }
  if (form.heatRequired !== null && typeof current.operations.heatDeclarationRequired === 'boolean') {
    if (form.heatRequired !== current.operations.heatDeclarationRequired) patch.heatDeclarationRequired = form.heatRequired;
  }
  // Ted #34 — only when the server carries the key; blank clears it (null).
  if (form.groomDayRate !== null && current.operations.groomDayHalfDaycareRateCents !== undefined) {
    const next = form.groomDayRate.trim() === '' ? null : dollarsToCents(form.groomDayRate);
    if (next === null || Number.isInteger(next)) {
      if (next !== current.operations.groomDayHalfDaycareRateCents) patch.groomDayHalfDaycareRateCents = next;
    }
  }
  if (form.firstGroomPercent !== null && current.operations.firstGroomDiscountPercent !== undefined) {
    const next = form.firstGroomPercent.trim() === '' ? null : Number(form.firstGroomPercent.trim());
    if (next === null || Number.isInteger(next)) {
      if (next !== current.operations.firstGroomDiscountPercent) patch.firstGroomDiscountPercent = next;
    }
  }
  // #948 — only when the server carries the keys; blank turns it off (null).
  if (form.multiDog && current.operations.multiDogDiscountPercent !== undefined) {
    const md = form.multiDog;
    const next = md.percent.trim() === '' ? null : Number(md.percent.trim());
    if (next === null || Number.isInteger(next)) {
      if (next !== current.operations.multiDogDiscountPercent) patch.multiDogDiscountPercent = next;
    }
    const currentServices = current.operations.multiDogServices ?? DEFAULT_MULTI_DOG_SERVICE_OPTIONS.map((o) => o.name);
    if (!sameNames(md.services, currentServices)) patch.multiDogServices = [...md.services];
    if (md.appliesTo !== (current.operations.multiDogAppliesTo ?? 'every_dog')) {
      patch.multiDogAppliesTo = md.appliesTo;
    }
  }
  // Sawyer 10-30 — only when the server carries the key.
  if (form.runCardOff && Array.isArray(current.operations.runCardPromptOffServices)) {
    if (!sameNames(form.runCardOff, current.operations.runCardPromptOffServices)) {
      patch.runCardPromptOffServices = [...form.runCardOff];
    }
  }
  // SPEC 2026-10-26 — only when the server carries the key (an older server never sees it).
  if (typeof form.verifiedOnly === 'boolean' && typeof current.operations.vaccinationVerifiedRecordsOnly === 'boolean') {
    if (form.verifiedOnly !== current.operations.vaccinationVerifiedRecordsOnly) {
      patch.vaccinationVerifiedRecordsOnly = form.verifiedOnly;
    }
  }
  if (typeof form.vetRequired === 'boolean' && typeof current.operations.vetInfoRequiredForBooking === 'boolean') {
    if (form.vetRequired !== current.operations.vetInfoRequiredForBooking) {
      patch.vetInfoRequiredForBooking = form.vetRequired;
    }
  }
  return patch;
}

export function validateForm(form: FormState, limits: TenantOperationsSettings['limits']): string | null {
  const cap = Number(form.capacity);
  if (!Number.isInteger(cap) || cap < 1 || cap > limits.maxFacilityCapacity) {
    return `Total dogs on site must be a whole number between 1 and ${limits.maxFacilityCapacity}.`;
  }
  if (form.ratio.trim() !== '') {
    const r = Number(form.ratio);
    if (!Number.isInteger(r) || r < 1 || r > limits.maxStaffingDogsPerHandler) {
      return `Dogs per handler must be a whole number between 1 and ${limits.maxStaffingDogsPerHandler}, or left blank.`;
    }
  }
  // #926 — the rate is money: dollars and cents, never negative, never absurd.
  const maxRate = limits.maxMealTrackingRateCents ?? 10_000;
  const rate = dollarsToCents(form.mealRate);
  if (!Number.isInteger(rate) || rate < 0 || rate > maxRate) {
    return `Meal tracking rate must be a dollar amount between $0.00 and $${centsToDollars(maxRate)} per day.`;
  }
  if (form.mealCharge && rate === 0) {
    return 'Set a per-day rate above $0.00 to charge for meal tracking, or turn the charge off.';
  }
  // #914 — the pair must be coherent BEFORE the save: a grace earlier than
  // the posted checkout time would propose a charge for a dog collected on
  // time. The server refuses it too; catching it here means the owner sees a
  // sentence instead of a round trip.
  const checkout = localTimeMinutes(form.boardingCheckout);
  const grace = localTimeMinutes(form.boardingGrace);
  if (Number.isNaN(checkout)) return 'Boarding checkout time must be a time like 10:30.';
  if (Number.isNaN(grace)) return 'Late pickup time must be a time like 11:00.';
  if (grace < checkout) {
    return 'The late pickup time must be at or after the boarding checkout time.';
  }
  // Ted #34 — money and a percentage; blank is a real answer ("not offered" / "off").
  if (form.groomDayRate !== null && form.groomDayRate.trim() !== '') {
    const maxGroomDay = limits.maxGroomDayHalfDaycareRateCents ?? 10_000;
    const cents = dollarsToCents(form.groomDayRate);
    if (!Number.isInteger(cents) || cents < 0 || cents > maxGroomDay) {
      return `Half-day daycare with a groom must be a dollar amount between $0.00 and $${centsToDollars(maxGroomDay)}, or left blank to not offer it.`;
    }
  }
  if (form.firstGroomPercent !== null && form.firstGroomPercent.trim() !== '') {
    const maxPct = limits.maxFirstGroomDiscountPercent ?? 100;
    const pct = Number(form.firstGroomPercent.trim());
    if (!Number.isInteger(pct) || pct < 1 || pct > maxPct) {
      return `First-groom discount must be a whole percent between 1 and ${maxPct}, or left blank to turn it off.`;
    }
  }
  if (form.multiDog && form.multiDog.percent.trim() !== '') {
    const maxPct = limits.maxMultiDogDiscountPercent ?? 100;
    const pct = Number(form.multiDog.percent.trim());
    if (!Number.isInteger(pct) || pct < 1 || pct > maxPct) {
      return `Multi-dog discount must be a whole percent between 1 and ${maxPct}, or left blank to turn it off.`;
    }
    if (form.multiDog.services.length === 0) {
      return 'Tick at least one service for the multi-dog discount, or leave the percent blank to turn it off.';
    }
  }
  return null;
}

/**
 * #948 — the checklist rows: the tenant's own active services (by display
 * name) when the catalogue read worked, else Ted's three; plus any name the
 * setting holds that the list lacks, so it can always be unticked.
 */
export function multiDogServiceOptions(
  catalogue: ReadonlyArray<{ name: string; displayName: string; isActive: boolean }> | null,
  selected: ReadonlyArray<string>,
): Array<{ name: string; label: string }> {
  const base =
    catalogue && catalogue.length > 0
      ? catalogue.filter((c) => c.isActive).map((c) => ({ name: c.name.trim().toLowerCase(), label: c.displayName }))
      : DEFAULT_MULTI_DOG_SERVICE_OPTIONS.map((o) => ({ ...o }));
  const seen = new Set(base.map((o) => o.name));
  for (const n of selected) {
    const key = n.trim().toLowerCase();
    if (!seen.has(key)) {
      base.push({ name: key, label: key });
      seen.add(key);
    }
  }
  return base;
}

/**
 * Sawyer 10-30 — the run-card checklist. The same rows as the multi-dog list,
 * minus transport: a leg-only service never checks in on its own, so there is
 * no check-in to ask at.
 */
export function runCardServiceOptions(
  catalogue: ReadonlyArray<{ name: string; displayName: string; isActive: boolean }> | null,
  off: ReadonlyArray<string>,
): Array<{ name: string; label: string }> {
  return multiDogServiceOptions(catalogue, off).filter((o) => !isLegOnlyServiceName(o.name));
}

function humanList(keys: ReadonlyArray<TenantOperationsKey>): string {
  const labels = keys.map((k) => OPERATIONS_KEY_LABELS[k]);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function OperationsSettingsCard({
  apiUrl,
  canEdit,
  canEditCharges = canEdit,
  canEditVaccinations = canEdit,
}: OperationsSettingsCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [settings, setSettings] = useState<TenantOperationsSettings | null>(null);
  const [form, setForm] = useState<FormState>({ capacity: '', gate: 'warn', careLogParentVisible: false, ratio: '', mealCharge: false, mealRate: '5.00', lateCharge: false, boardingCheckout: DEFAULT_BOARDING_CHECKOUT_LOCAL_TIME, boardingGrace: DEFAULT_BOARDING_LATE_GRACE_LOCAL_TIME, vaccineTerms: null, commingling: null, heatRequired: null, groomDayRate: null, firstGroomPercent: null, multiDog: null, runCardOff: null, verifiedOnly: null, vetRequired: null });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ changedKeys: TenantOperationsKey[] } | null>(null);
  /** Today's on-site count, so the owner sets the limit against reality. Best-effort. */
  const [onSiteNow, setOnSiteNow] = useState<number | null>(null);
  /** #948 — the tenant's own services for the multi-dog checklist. Best-effort; null = use Ted's three. */
  const [serviceCatalogue, setServiceCatalogue] = useState<
    ReadonlyArray<{ name: string; displayName: string; isActive: boolean }> | null
  >(null);
  /**
   * SPEC 2026-10-26 — how many vaccines the list requires, reported by the
   * section (which loads on its own). null = not known (loading, failed, an
   * older server): the "Block has no effect" note never shows on a guess.
   */
  const [requirementCount, setRequirementCount] = useState<number | null>(null);

  const apply = useCallback((s: TenantOperationsSettings) => {
    setSettings(s);
    setForm(formFromSettings(s));
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    setLoadError(null);
    setSaved(null);
    const res = await adminTenantSettingsApi.getOperations(apiUrl);
    if (res.error || !res.data) {
      setLoadError(res.error ?? 'Could not load operations settings.');
      setStatus('error');
      return;
    }
    apply(res.data);
    setStatus('ready');
  }, [apiUrl, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ onSite?: number }>(apiUrl, `/api/admin/dashboard/facility-status?date=${todayIso()}`)
      .then((res) => {
        if (cancelled) return;
        const n = res.data?.onSite;
        setOnSiteNow(typeof n === 'number' && Number.isFinite(n) ? n : null);
      })
      .catch(() => {
        if (!cancelled) setOnSiteNow(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  useEffect(() => {
    let cancelled = false;
    void adminTenantServicesApi
      .list(apiUrl)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.services;
        setServiceCatalogue(Array.isArray(list) ? list : null);
      })
      .catch(() => {
        if (!cancelled) setServiceCatalogue(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const patch = useMemo(
    () => (settings ? patchFromForm(form, settings) : {}),
    [form, settings],
  );
  const isDirty = Object.keys(patch).length > 0;
  const allDefault =
    settings !== null &&
    settings.source.defaultFacilityCapacity === 'default' &&
    settings.source.vaccinationBookingGate === 'default' &&
    settings.source.staffingDogsPerHandler === 'default' &&
    !settings.operations.careLogParentVisible;

  const handleSave = useCallback(async () => {
    if (!settings) return;
    setSaveError(null);
    setSaved(null);
    const invalid = validateForm(form, settings.limits);
    if (invalid) {
      setSaveError(invalid);
      return;
    }
    const body = patchFromForm(form, settings);
    if (Object.keys(body).length === 0) return;
    setIsSaving(true);
    const res = await adminTenantSettingsApi.updateOperations(apiUrl, body);
    setIsSaving(false);
    if (res.error || !res.data) {
      setSaveError(res.error ?? 'Could not save. Please try again.');
      return;
    }
    const { changedKeys, audited: _audited, ...view } = res.data;
    apply(view);
    setSaved({ changedKeys });
  }, [apiUrl, apply, form, settings]);

  const handleReset = useCallback(() => {
    if (settings) setForm(formFromSettings(settings));
    setSaveError(null);
    setSaved(null);
  }, [settings]);

  // ── Loading (skeleton, never a spinner wall) ────────────────────────
  if (status === 'loading') {
    return (
      <Card title="Operations">
        <div data-testid="operations-settings-loading">
          <Skeleton variant="text" lines={5} />
        </div>
      </Card>
    );
  }

  // ── Error (honest, with recovery) ───────────────────────────────────
  if (status === 'error' || !settings) {
    return (
      <Card title="Operations">
        <div data-testid="operations-settings-error">
          <Alert variant="error">{loadError ?? 'Could not load operations settings.'}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const { operations, source, limits, lastChange } = settings;
  // Wrapped in a span: @ferro/ui's Badge does not forward data-* props, and
  // the mark is what the tests (unit + Playwright) key on.
  const defaultMark = (key: keyof TenantOperationsSettings['source']): JSX.Element | null =>
    source[key] === 'default' ? (
      <span data-testid={`operations-default-${key}`}>
        <Badge variant="platform">Platform default</Badge>
      </span>
    ) : null;
  // #1104 — per vaccine. No mark when the server sends no per-vaccine
  // provenance: an unknown is not a claim that it is the default.
  const vaccineDefaultMark = (key: TenantVaccineTermKey): JSX.Element | null =>
    settings.vaccineTermsSource?.[key] === 'default' ? (
      <span data-testid={`operations-default-vaccineTerms-${key}`}>
        <Badge variant="platform">Platform default</Badge>
      </span>
    ) : null;
  const vaccineTermChoices = termChoicesFrom(operations.vaccineTerms);

  // #933 D2 — the charges, stated: in the read-only branch for a role that
  // cannot save the card, and inside the form for one that cannot save these.
  const chargesNote =
    canEdit && !canEditCharges ? (
      <span className="ferro-muted ferro-settings__readonly-hint">{OWNER_ONLY_NOTE}</span>
    ) : null;
  const latePickupRow = (
    <div className="ferro-settings__readonly-row">
      <dt>Late pickup on boarding stays</dt>
      <dd data-testid="operations-readonly-late-pickup">
        {operations.boardingLatePickupChargeEnabled === true
          ? `Offered after ${operations.boardingLateGraceLocalTime ?? DEFAULT_BOARDING_LATE_GRACE_LOCAL_TIME} (checkout ${operations.boardingCheckoutLocalTime ?? DEFAULT_BOARDING_CHECKOUT_LOCAL_TIME})`
          : `Off (checkout ${operations.boardingCheckoutLocalTime ?? DEFAULT_BOARDING_CHECKOUT_LOCAL_TIME})`}{' '}
        {defaultMark('boardingLatePickupChargeEnabled')}
        {chargesNote}
      </dd>
    </div>
  );
  const mealChargeRow = (
    <div className="ferro-settings__readonly-row">
      <dt>Meal tracking charge on boarding stays</dt>
      <dd data-testid="operations-readonly-meal-charge">
        {operations.mealTrackingChargeEnabled === true
          ? `On — $${centsToDollars(operations.mealTrackingRateCents ?? DEFAULT_MEAL_TRACKING_RATE_CENTS)} per day, per dog`
          : 'Off'}{' '}
        {defaultMark('mealTrackingChargeEnabled')}
        {chargesNote}
      </dd>
    </div>
  );
  const multiDogRow =
    operations.multiDogDiscountPercent !== undefined ? (
      <div className="ferro-settings__readonly-row">
        <dt>Multi-dog discount</dt>
        <dd data-testid="operations-readonly-multi-dog">
          {operations.multiDogDiscountPercent === null
            ? 'Off'
            : `${operations.multiDogDiscountPercent}% off ${
                operations.multiDogAppliesTo === 'additional_dogs' ? 'every dog after the first' : 'every dog'
              } when two or more of a family's dogs are here together — ${multiDogServiceOptions(
                serviceCatalogue,
                operations.multiDogServices ?? [],
              )
                .filter((o) => (operations.multiDogServices ?? []).includes(o.name))
                .map((o) => o.label)
                .join(', ') || 'no services'}`}
          {chargesNote}
        </dd>
      </div>
    ) : null;

  return (
    <Card
      title="Operations"
      subtitle="The numbers your booking engine and customer app run on. Change them here and the next booking honors them."
    >
      <div className="ferro-settings__card" data-testid="operations-settings-ready">
        {/* Last change — the audit row, on the card, not in a toast (Principle 5). */}
        {lastChange ? (
          <p className="ferro-settings__last-change" data-testid="operations-last-change">
            Last changed by <strong>{lastChange.byName}</strong>
            {lastChange.at ? ` on ${whenLabel(lastChange.at)}` : ''}
            {lastChange.changedKeys.length > 0 ? ` — ${humanList(lastChange.changedKeys)}` : ''}
          </p>
        ) : allDefault ? (
          <p className="ferro-settings__teaching" data-testid="operations-settings-empty">
            You&apos;re on the platform defaults. Set the numbers your building actually runs on —
            the total on-site limit is the one that stops a booking.
          </p>
        ) : null}

        {/* R2 + R23 — read-only for every role that can see this card; only
            rendered when the server sends it (older servers omit it). */}
        {settings.onlinePayments ? <OnlinePaymentsModeNote onlinePayments={settings.onlinePayments} /> : null}

        {canEdit ? (
          <form
            className="ferro-form ferro-settings__form"
            // noValidate: the bounds come from the SERVER's limits and are
            // explained in our own words under the field; the browser's native
            // bubble would pre-empt that copy (and differs per browser/locale).
            // validateForm() runs on every submit instead.
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void handleSave();
            }}
            aria-label="Operations settings"
          >
            <div className="ferro-settings__field">
              <Input
                name="defaultFacilityCapacity"
                label="Total dogs on site"
                type="number"
                inputMode="numeric"
                min={1}
                max={limits.maxFacilityCapacity}
                step={1}
                value={form.capacity}
                onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
                helperText="A hard stop. Daycare, boarding and assessments count toward it; grooming and walks do not."
                data-testid="operations-capacity"
              />
              <div className="ferro-settings__field-meta">
                {defaultMark('defaultFacilityCapacity')}
                {onSiteNow !== null ? (
                  <span className="ferro-muted" data-testid="operations-on-site-now">
                    {onSiteNow} on site now
                  </span>
                ) : null}
              </div>
            </div>

            <div className="ferro-settings__field">
              <Select
                name="vaccinationBookingGate"
                label="Vaccination gate for online booking"
                value={form.gate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, gate: e.target.value as TenantVaccinationGate }))
                }
                options={GATE_OPTIONS}
                helperText={GATE_DESCRIPTIONS[form.gate]}
                data-testid="operations-gate"
              />
              <div className="ferro-settings__field-meta">{defaultMark('vaccinationBookingGate')}</div>
              {form.gate === 'block' && requirementCount === 0 ? (
                <p className="ferro-vax-req__gate-note" data-testid="operations-gate-block-empty">
                  {GATE_BLOCK_EMPTY_NOTE}
                </p>
              ) : null}
            </div>

            {/* SPEC 2026-10-26 Story 1 — the list the gate enforces, beside
                it. Its own fetch, its own states; its editor keeps Enter to
                itself so it never submits this form. */}
            <VaccinationRequirementsSection
              apiUrl={apiUrl}
              canEdit={canEditVaccinations}
              onRequirementCountChange={setRequirementCount}
            />

            {/* SPEC 2026-10-26 D4 and Story 5 — switches hidden on
                a server that does not carry them (the heat-question pattern). */}
            {typeof form.verifiedOnly === 'boolean' ? (
              <div className="ferro-settings__field" data-testid="operations-verified-only">
                <Checkbox
                  name="vaccinationVerifiedRecordsOnly"
                  label={VERIFIED_ONLY_LABEL}
                  description={VERIFIED_ONLY_DESCRIPTION}
                  checked={form.verifiedOnly}
                  onChange={(e) => setForm((f) => ({ ...f, verifiedOnly: e.target.checked }))}
                  data-testid="operations-verified-only-toggle"
                />
                <div className="ferro-settings__field-meta">{defaultMark('vaccinationVerifiedRecordsOnly')}</div>
              </div>
            ) : null}
            {typeof form.vetRequired === 'boolean' ? (
              <div className="ferro-settings__field" data-testid="operations-vet-required">
                <Checkbox
                  name="vetInfoRequiredForBooking"
                  label={VET_REQUIRED_LABEL}
                  description={VET_REQUIRED_DESCRIPTION}
                  checked={form.vetRequired}
                  onChange={(e) => setForm((f) => ({ ...f, vetRequired: e.target.checked }))}
                  data-testid="operations-vet-required-toggle"
                />
                <div className="ferro-settings__field-meta">{defaultMark('vetInfoRequiredForBooking')}</div>
              </div>
            ) : null}

            <div className="ferro-settings__field">
              <Checkbox
                name="careLogParentVisible"
                label="Show meal tracking to pet parents"
                description="When on, parents see the meal outcomes your staff marked shareable in the customer app. Medication notes are never shown, whatever this says."
                checked={form.careLogParentVisible}
                onChange={(e) => setForm((f) => ({ ...f, careLogParentVisible: e.target.checked }))}
                data-testid="operations-care-log"
              />
            </div>

            {canEditCharges ? (
              <>
                <div className="ferro-settings__field">
                  <Checkbox
                    name="mealTrackingChargeEnabled"
                    label="Charge for meal tracking on boarding stays"
                    description="When on, every boarding check-out carries a meal-tracking line for each day of the stay, arrival through departure, per dog, at the rate below. A day that already has your meal add-on on it (House Food, marked “This is the daily meal charge” in the grooming menu) is not charged again. It shows on the invoice with its reason; the desk can still change it with a reason."
                    checked={form.mealCharge}
                    onChange={(e) => setForm((f) => ({ ...f, mealCharge: e.target.checked }))}
                    data-testid="operations-meal-charge"
                  />
                  <div className="ferro-settings__field-meta">{defaultMark('mealTrackingChargeEnabled')}</div>
                </div>

                <div className="ferro-settings__field">
                  <Input
                    name="mealTrackingRateCents"
                    label="Meal tracking rate per day (dollars)"
                    type="text"
                    inputMode="decimal"
                    value={form.mealRate}
                    placeholder="5.00"
                    onChange={(e) => setForm((f) => ({ ...f, mealRate: e.target.value }))}
                    helperText="Ted's number is $5.00. Per day, per dog, on boarding stays only: a Friday-to-Monday stay is four days."
                    data-testid="operations-meal-rate"
                  />
                  <div className="ferro-settings__field-meta">{defaultMark('mealTrackingRateCents')}</div>
                </div>

                {/* Ted #11 (#914) — the boarding checkout cutoff. Off by
                    default: no tenant inherits HTHD's 10:30 by accident, and
                    even switched on it only ever OFFERS the charge. */}
                <div className="ferro-settings__field">
                  <Checkbox
                    name="boardingLatePickupChargeEnabled"
                    label="Offer a daycare charge when a boarder is picked up late"
                    description="When on, a boarding check-out after the late pickup time below shows the desk a Late pickup line: add a half day or a full day of daycare for each dog, use the client's package days instead, or waive it with a note. Nothing is ever charged without someone choosing it, and the price can still be adjusted afterwards."
                    checked={form.lateCharge}
                    onChange={(e) => setForm((f) => ({ ...f, lateCharge: e.target.checked }))}
                    data-testid="operations-late-charge"
                  />
                  <div className="ferro-settings__field-meta">
                    {defaultMark('boardingLatePickupChargeEnabled')}
                  </div>
                </div>

                <div className="ferro-settings__field">
                  <Input
                    name="boardingCheckoutLocalTime"
                    label="Boarding checkout time"
                    type="time"
                    value={form.boardingCheckout}
                    onChange={(e) => setForm((f) => ({ ...f, boardingCheckout: e.target.value }))}
                    helperText="The time you post to clients. Ted's is 10:30 AM."
                    data-testid="operations-boarding-checkout"
                  />
                  <div className="ferro-settings__field-meta">
                    {defaultMark('boardingCheckoutLocalTime')}
                  </div>
                </div>

                <div className="ferro-settings__field">
                  <Input
                    name="boardingLateGraceLocalTime"
                    label="Late pickup time"
                    type="time"
                    value={form.boardingGrace}
                    onChange={(e) => setForm((f) => ({ ...f, boardingGrace: e.target.value }))}
                    helperText="Past this time the desk is offered the half-day or full-day charge. The grace after checkout — e.g. 11:30 AM for an hour after a 10:30 checkout."
                    data-testid="operations-boarding-grace"
                  />
                  <div className="ferro-settings__field-meta">
                    {defaultMark('boardingLateGraceLocalTime')}
                  </div>
                </div>
              </>
            ) : (
              <dl className="ferro-settings__readonly" data-testid="operations-charges-readonly">
                {mealChargeRow}
                {latePickupRow}
              </dl>
            )}

            {/* Ted #34 E6.1 / E6.2 (#1161) — grooming-day pricing, both blank
                = off: no tenant inherits HTHD's $25 / 25 % by accident. Hidden
                on a server that does not carry the keys. */}
            {form.groomDayRate !== null ? (
              <div className="ferro-settings__field">
                <Input
                  name="groomDayHalfDaycareRateCents"
                  label="Half-day daycare with a groom (dollars per dog)"
                  type="text"
                  inputMode="decimal"
                  value={form.groomDayRate}
                  placeholder="Leave blank to not offer it"
                  onChange={(e) => setForm((f) => ({ ...f, groomDayRate: e.target.value }))}
                  helperText="When set, booking a groom offers “Add half-day daycare” at this price, as one visit. Half-day only — no full day with a groom. Ted's is $25.00."
                  data-testid="operations-groom-day-rate"
                />
                <div className="ferro-settings__field-meta">{defaultMark('groomDayHalfDaycareRateCents')}</div>
              </div>
            ) : null}
            {form.firstGroomPercent !== null ? (
              <div className="ferro-settings__field">
                <Input
                  name="firstGroomDiscountPercent"
                  label="First-groom discount (percent)"
                  type="text"
                  inputMode="numeric"
                  value={form.firstGroomPercent}
                  placeholder="Leave blank for no discount"
                  onChange={(e) => setForm((f) => ({ ...f, firstGroomPercent: e.target.value }))}
                  helperText="Taken off a dog's first groom with you, automatically at checkout, as its own line with the reason. The desk can remove it with a note. Dogs whose grooming history came from Gingr without a dog name are not discounted automatically. Ted's is 25%."
                  data-testid="operations-first-groom"
                />
                <div className="ferro-settings__field-meta">{defaultMark('firstGroomDiscountPercent')}</div>
              </div>
            ) : null}

            {/* #948 — Ted, 10-09: "10% … off every dog … every daycare and
                overnight." Blank = off: no tenant inherits HTHD's 10 % by
                accident. Hidden on a server that does not carry the keys. */}
            {!canEditCharges ? (
              multiDogRow ? (
                <dl className="ferro-settings__readonly" data-testid="operations-multi-dog-readonly">
                  {multiDogRow}
                </dl>
              ) : null
            ) : form.multiDog ? (
              <fieldset className="ferro-settings__fieldset" data-testid="operations-multi-dog">
                <legend className="ferro-settings__subheading">Multi-dog discount</legend>
                <p className="ferro-muted ferro-settings__footnote">
                  When two or more dogs from one family are here together, this comes off each visit
                  automatically at checkout, as its own line with the reason. The desk can remove it
                  with a note. Dogs on different services count together (a boarder and a daycare
                  sibling the same day). Visits brought over from Gingr already carry Gingr&apos;s
                  discount and are left alone.
                </p>
                <div className="ferro-settings__field">
                  <Input
                    name="multiDogDiscountPercent"
                    label="Multi-dog discount (percent)"
                    type="text"
                    inputMode="numeric"
                    value={form.multiDog.percent}
                    placeholder="Leave blank for no discount"
                    onChange={(e) =>
                      setForm((f) => (f.multiDog ? { ...f, multiDog: { ...f.multiDog, percent: e.target.value } } : f))
                    }
                    helperText="Ted's is 10%."
                    data-testid="operations-multi-dog-percent"
                  />
                  <div className="ferro-settings__field-meta">{defaultMark('multiDogDiscountPercent')}</div>
                </div>
                <p className="ferro-settings__label">Applies to these services</p>
                {multiDogServiceOptions(serviceCatalogue, form.multiDog.services).map((opt) => (
                  <div className="ferro-settings__field" key={opt.name}>
                    <Checkbox
                      name={`multiDogServices.${opt.name}`}
                      label={opt.label}
                      checked={form.multiDog!.services.includes(opt.name)}
                      onChange={(e) =>
                        setForm((f) => {
                          if (!f.multiDog) return f;
                          const next = e.target.checked
                            ? [...new Set([...f.multiDog.services, opt.name])]
                            : f.multiDog.services.filter((n) => n !== opt.name);
                          return { ...f, multiDog: { ...f.multiDog, services: next } };
                        })
                      }
                      data-testid={`operations-multi-dog-service-${opt.name}`}
                    />
                  </div>
                ))}
                <div className="ferro-settings__field-meta">{defaultMark('multiDogServices')}</div>
                <p className="ferro-settings__label">Who gets it</p>
                {MULTI_DOG_APPLIES_TO_OPTIONS.map((opt) => (
                  <label className="ferro-settings__field ferro-settings__radio" key={opt.value}>
                    <input
                      type="radio"
                      name="multiDogAppliesTo"
                      value={opt.value}
                      checked={form.multiDog!.appliesTo === opt.value}
                      onChange={() =>
                        setForm((f) => (f.multiDog ? { ...f, multiDog: { ...f.multiDog, appliesTo: opt.value } } : f))
                      }
                      data-testid={`operations-multi-dog-applies-${opt.value}`}
                    />{' '}
                    <span>
                      <strong>{opt.label}</strong> — {opt.description}
                    </span>
                  </label>
                ))}
                <div className="ferro-settings__field-meta">{defaultMark('multiDogAppliesTo')}</div>
              </fieldset>
            ) : null}

            {/* Sawyer, 10-30: "I don't need the check in box for run cards when
                I check in for daycare." One tick per service: ticked asks at
                check-in, unticked does not. The card itself stays printable.
                Hidden on a server that does not carry the key. */}
            {form.runCardOff ? (
              <fieldset className="ferro-settings__fieldset" data-testid="operations-run-card">
                <legend className="ferro-settings__subheading">Ask to print a run card at check-in</legend>
                <p className="ferro-muted ferro-settings__footnote">
                  After a dog checks in for a ticked service, the desk is asked to print its run card.
                  Unticked services skip the question. Any checked-in dog&apos;s card can still be
                  printed from its row: Run card, under Departures on the Dashboard or under Show
                  checkout on Front Desk.
                </p>
                {runCardServiceOptions(serviceCatalogue, form.runCardOff).map((opt) => (
                  <div className="ferro-settings__field" key={opt.name}>
                    <Checkbox
                      name={`runCardPrompt.${opt.name}`}
                      label={opt.label}
                      checked={!form.runCardOff!.includes(opt.name)}
                      onChange={(e) =>
                        setForm((f) => {
                          if (!f.runCardOff) return f;
                          const next = e.target.checked
                            ? f.runCardOff.filter((n) => n !== opt.name)
                            : [...new Set([...f.runCardOff, opt.name])];
                          return { ...f, runCardOff: next };
                        })
                      }
                      data-testid={`operations-run-card-${opt.name}`}
                    />
                  </div>
                ))}
                <div className="ferro-settings__field-meta">{defaultMark('runCardPromptOffServices')}</div>
              </fieldset>
            ) : null}

            {/* LG (#1111) — Ted, 2026-09-29: "we do not accept female dogs who
                are in heat … a hard stop". ON by default (L0, 2026-09-30); a
                facility that accepts females in heat switches it off here.
                Hidden on a server that does not carry the key. */}
            {form.heatRequired !== null ? (
              <div className="ferro-settings__field" data-testid="operations-heat">
                <Checkbox
                  name="heatDeclarationRequired"
                  label="Ask “Is your dog currently in heat?” at reservation"
                  description="When on, every reservation — online and at the desk — asks the question for each dog not known to be male and not known to be spayed. A Yes is refused with a clear notice; a No is recorded on the reservation and staff still confirm at drop-off."
                  checked={form.heatRequired}
                  onChange={(e) => setForm((f) => ({ ...f, heatRequired: e.target.checked }))}
                  data-testid="operations-heat-required"
                />
                <div className="ferro-settings__field-meta">{defaultMark('heatDeclarationRequired')}</div>
              </div>
            ) : null}

            {/* LG (#1113) — Ted, 2026-09-29: the intact male + intact female
                review fires only when they would share a play / common area.
                Which services count is the owner's call. */}
            {form.commingling ? (
              <fieldset className="ferro-settings__fieldset" data-testid="operations-commingling">
                <legend className="ferro-settings__subheading">Services where dogs commingle</legend>
                <p className="ferro-muted ferro-settings__footnote">
                  When an intact male and an intact female would both be on one of these services the
                  same day, the reservation lands pending for staff review. Dogs on other services, or
                  in the building on the same day, are never flagged for this.
                </p>
                {COMMINGLING_SERVICE_OPTIONS.map((opt) => (
                  <div className="ferro-settings__field" key={opt.name}>
                    <Checkbox
                      name={`comminglingServiceNames.${opt.name}`}
                      label={opt.label}
                      checked={form.commingling!.includes(opt.name)}
                      onChange={(e) =>
                        setForm((f) => {
                          if (!f.commingling) return f;
                          const next = e.target.checked
                            ? [...new Set([...f.commingling, opt.name])]
                            : f.commingling.filter((n) => n !== opt.name);
                          return { ...f, commingling: next };
                        })
                      }
                      data-testid={`operations-commingling-${opt.name}`}
                    />
                  </div>
                ))}
                <div className="ferro-settings__field-meta">{defaultMark('comminglingServiceNames')}</div>
              </fieldset>
            ) : null}

            {/* #1104 — Ted, 2026-09-25. One select per vaccine; hidden on a
                server that does not carry the setting (deploy skew). */}
            {form.vaccineTerms ? (
              <fieldset
                className="ferro-settings__fieldset"
                data-testid="operations-vaccine-terms"
              >
                <legend className="ferro-settings__subheading">Vaccine durations</legend>
                <p className="ferro-muted ferro-settings__footnote">
                  Vets don&apos;t always print an expiration date. Tell us how long each shot
                  lasts at your facility and the vaccination inbox works the date out from
                  when it was given. A date the vet printed always wins.
                </p>
                {VACCINE_TERM_KEYS.map((key) => {
                  const choice = form.vaccineTerms![key];
                  return (
                    <div className="ferro-settings__field" key={key}>
                      <Select
                        name={`vaccineTerms.${key}`}
                        label={VACCINE_TERM_VACCINE_LABELS[key]}
                        value={choice}
                        onChange={(e) =>
                          setForm((f) =>
                            f.vaccineTerms
                              ? {
                                  ...f,
                                  vaccineTerms: {
                                    ...f.vaccineTerms,
                                    [key]: e.target.value as VaccineTermChoice,
                                  },
                                }
                              : f,
                          )
                        }
                        options={VACCINE_TERM_OPTIONS}
                        helperText={VACCINE_TERM_DESCRIPTIONS[choice]}
                        data-testid={`operations-vaccine-term-${key}`}
                      />
                      <div className="ferro-settings__field-meta">{vaccineDefaultMark(key)}</div>
                    </div>
                  );
                })}
              </fieldset>
            ) : null}

            <div className="ferro-settings__field">
              <Input
                name="staffingDogsPerHandler"
                label="Dogs per handler"
                type="number"
                inputMode="numeric"
                min={1}
                max={limits.maxStaffingDogsPerHandler}
                step={1}
                value={form.ratio}
                placeholder="Not set"
                onChange={(e) => setForm((f) => ({ ...f, ratio: e.target.value }))}
                helperText="Recorded for the daily staffing view. Nothing blocks on it yet; leave blank to keep it unset."
                data-testid="operations-ratio"
              />
              <div className="ferro-settings__field-meta">{defaultMark('staffingDogsPerHandler')}</div>
            </div>

            {saveError ? (
              <div data-testid="operations-save-error">
                <Alert variant="error">{saveError}</Alert>
              </div>
            ) : null}
            {saved && !saveError ? (
              <div data-testid="operations-save-success">
                <Alert variant="success">
                  {saved.changedKeys.length === 0
                    ? 'Saved. Nothing changed.'
                    : `Saved. Changed ${humanList(saved.changedKeys)}. The next booking honors it.`}
                </Alert>
              </div>
            ) : null}

            <div className="ferro-settings__actions">
              <Button
                type="submit"
                variant="primary"
                isLoading={isSaving}
                disabled={!isDirty || isSaving}
                data-testid="operations-save"
              >
                Save
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleReset}
                disabled={!isDirty || isSaving}
                data-testid="operations-reset"
              >
                Reset
              </Button>
            </div>
          </form>
        ) : (
          <>
            {/* One wrapper, three blocks: the gate, the list it enforces
                (editable by a manager, D1), then the rest of the card. */}
            <div className="ferro-settings__card" data-testid="operations-settings-readonly">
              <dl className="ferro-settings__readonly">
                <div className="ferro-settings__readonly-row">
                  <dt>Total dogs on site</dt>
                  <dd>
                    {operations.defaultFacilityCapacity} {defaultMark('defaultFacilityCapacity')}
                    {onSiteNow !== null ? (
                      <span className="ferro-muted"> · {onSiteNow} on site now</span>
                    ) : null}
                  </dd>
                </div>
                <div className="ferro-settings__readonly-row">
                  <dt>Vaccination gate</dt>
                  <dd data-testid="operations-readonly-gate">
                    {GATE_OPTIONS.find((o) => o.value === operations.vaccinationBookingGate)?.label ??
                      operations.vaccinationBookingGate}{' '}
                    {defaultMark('vaccinationBookingGate')}
                    <span className="ferro-muted ferro-settings__readonly-hint">
                      {GATE_DESCRIPTIONS[operations.vaccinationBookingGate]}
                    </span>
                    {canEditVaccinations ? (
                      <span className="ferro-muted ferro-settings__readonly-hint" data-testid="operations-gate-owner-only">
                        {OWNER_ONLY_NOTE}
                      </span>
                    ) : null}
                    {operations.vaccinationBookingGate === 'block' && requirementCount === 0 ? (
                      <span className="ferro-settings__readonly-hint ferro-vax-req__gate-note" data-testid="operations-gate-block-empty">
                        {GATE_BLOCK_EMPTY_NOTE}
                      </span>
                    ) : null}
                  </dd>
                </div>
              </dl>

              <VaccinationRequirementsSection
                apiUrl={apiUrl}
                canEdit={canEditVaccinations}
                onRequirementCountChange={setRequirementCount}
              />

              <dl className="ferro-settings__readonly">
                {/* SPEC 2026-10-26 D4 / Story 5 — stated for a non-owner; hidden on an older server. */}
                {typeof operations.vaccinationVerifiedRecordsOnly === 'boolean' ? (
                  <div className="ferro-settings__readonly-row">
                    <dt>{VERIFIED_ONLY_LABEL}</dt>
                    <dd data-testid="operations-readonly-verified-only">
                      {operations.vaccinationVerifiedRecordsOnly
                        ? 'On — a record a customer types in or sends counts once your staff check it'
                        : 'Off — any record on file counts'}{' '}
                      {defaultMark('vaccinationVerifiedRecordsOnly')}
                      {canEditVaccinations ? (
                        <span className="ferro-muted ferro-settings__readonly-hint">{OWNER_ONLY_NOTE}</span>
                      ) : null}
                    </dd>
                  </div>
                ) : null}
                {typeof operations.vetInfoRequiredForBooking === 'boolean' ? (
                  <div className="ferro-settings__readonly-row">
                    <dt>{VET_REQUIRED_LABEL}</dt>
                    <dd data-testid="operations-readonly-vet-required">
                      {operations.vetInfoRequiredForBooking
                        ? "On — customers add their vet when they book a dog's first visit online"
                        : 'Off'}{' '}
                      {defaultMark('vetInfoRequiredForBooking')}
                      {canEditVaccinations ? (
                        <span className="ferro-muted ferro-settings__readonly-hint">{OWNER_ONLY_NOTE}</span>
                      ) : null}
                    </dd>
                  </div>
                ) : null}
                <div className="ferro-settings__readonly-row">
                  <dt>Meal tracking visible to pet parents</dt>
                  <dd>{operations.careLogParentVisible ? 'On' : 'Off'}</dd>
                </div>
                <div className="ferro-settings__readonly-row">
                  <dt>Dogs per handler</dt>
                  <dd>
                    {operations.staffingDogsPerHandler ?? 'Not set'} {defaultMark('staffingDogsPerHandler')}
                  </dd>
                </div>
                {latePickupRow}
                {mealChargeRow}
                {/* Ted #34 — grooming-day pricing, stated for a non-owner. */}
                {operations.groomDayHalfDaycareRateCents !== undefined ? (
                  <div className="ferro-settings__readonly-row">
                    <dt>Half-day daycare with a groom</dt>
                    <dd data-testid="operations-readonly-groom-day">
                      {operations.groomDayHalfDaycareRateCents === null
                        ? 'Not offered'
                        : `$${centsToDollars(operations.groomDayHalfDaycareRateCents)} per dog`}
                    </dd>
                  </div>
                ) : null}
                {operations.firstGroomDiscountPercent !== undefined ? (
                  <div className="ferro-settings__readonly-row">
                    <dt>First-groom discount</dt>
                    <dd data-testid="operations-readonly-first-groom">
                      {operations.firstGroomDiscountPercent === null
                        ? 'Off'
                        : `${operations.firstGroomDiscountPercent}% off a dog's first groom, automatically at checkout`}
                    </dd>
                  </div>
                ) : null}
                {multiDogRow}
                {/* LG — the gate switches, stated for a non-owner. */}
                {typeof operations.heatDeclarationRequired === 'boolean' ? (
                  <div className="ferro-settings__readonly-row">
                    <dt>Heat question at reservation</dt>
                    <dd data-testid="operations-readonly-heat">
                      {operations.heatDeclarationRequired
                        ? 'On — every reservation asks for each dog not known to be male or spayed; a Yes is refused'
                        : 'Off'}{' '}
                      {defaultMark('heatDeclarationRequired')}
                    </dd>
                  </div>
                ) : null}
                {Array.isArray(operations.comminglingServiceNames) ? (
                  <div className="ferro-settings__readonly-row">
                    <dt>Services where dogs commingle</dt>
                    <dd data-testid="operations-readonly-commingling">
                      {operations.comminglingServiceNames.length === 0
                        ? 'None — the intact-pair review never fires by service'
                        : COMMINGLING_SERVICE_OPTIONS.filter((o) => operations.comminglingServiceNames!.includes(o.name))
                            .map((o) => o.label)
                            .join(', ') || operations.comminglingServiceNames.join(', ')}{' '}
                      {defaultMark('comminglingServiceNames')}
                    </dd>
                  </div>
                ) : null}
                {Array.isArray(operations.runCardPromptOffServices) ? (
                  <div className="ferro-settings__readonly-row">
                    <dt>Services that skip the run card question</dt>
                    <dd data-testid="operations-readonly-run-card">
                      {runCardServiceOptions(serviceCatalogue, operations.runCardPromptOffServices)
                        .filter((o) => operations.runCardPromptOffServices!.includes(o.name))
                        .map((o) => o.label)
                        .join(', ') || 'None — every check-in asks'}{' '}
                      {defaultMark('runCardPromptOffServices')}
                    </dd>
                  </div>
                ) : null}
                {vaccineTermChoices ? (
                  <div className="ferro-settings__readonly-row">
                    <dt>Vaccine durations</dt>
                    <dd data-testid="operations-readonly-vaccine-terms">
                      {VACCINE_TERM_KEYS.map((key) => (
                        <span key={key} className="ferro-settings__readonly-hint">
                          {VACCINE_TERM_VACCINE_LABELS[key]}:{' '}
                          {VACCINE_TERM_OPTIONS.find((o) => o.value === vaccineTermChoices[key])?.label}{' '}
                          {vaccineDefaultMark(key)}
                        </span>
                      ))}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
            <p className="ferro-muted" data-testid="operations-readonly-note">
              {canEditVaccinations
                ? 'Only an owner can change these settings. You can change the required vaccinations.'
                : 'Only an owner or manager can change these settings.'}
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
